import { FIELDS_MAP } from '@ubeya/shared-web/hooks/useFormFields';
import { FieldTypes } from '@ubeya/shared/hooks/account/useFields';
import { mappedArray } from '@ubeya/shared/utils/array';

/**
 * Calculate the count of completed timesheet fields from mappedTimesheetFieldsValues
 * @param {Object} row - The row object containing mappedTimesheetFieldsValues
 * @returns {number} - The count of completed (truthy) fields
 */
export const getTimesheetFieldsCompletedCount = (row) => {
  if (!row || !row.mappedTimesheetFieldsValues) {
    return 0;
  }

  const { mappedTimesheetFieldsValues } = row;

  // Count completed fields (fields with truthy values)
  return Object.entries(mappedTimesheetFieldsValues).filter(([, value]) => Boolean(value)).length;
};

/**
 * Format a timesheet field value for export (CSV/Excel)
 * @param {Object} field - The field definition
 * @param {any} value - The field value
 * @param {Object} options - Options containing t (translation function) and dateFormat
 * @returns {string} - The formatted value for export
 */
export const formatTimesheetFieldValueForExport = (field, value, { t, dateFormat }) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  // Handle boolean fields
  if (field.fieldTypeId === FieldTypes.FIELD_TYPE_BOOLEAN) {
    return value ? t('yes') : t('no');
  }

  // Handle document/signature fields
  if ([FieldTypes.FIELD_TYPE_DOCUMENT, FieldTypes.FIELD_TYPE_SIGNATURE].includes(field.fieldTypeId)) {
    return value?.link || '';
  }

  // Use FIELDS_MAP formatter with mapOptions for dropdowns, dates, etc.
  const mapOptions =
    field.options &&
    mappedArray(
      field.options,
      ({ id: optionId }) => optionId,
      ({ title }) => title
    );

  const formatter = FIELDS_MAP[field.fieldTypeId]?.viewFormatter;
  if (formatter) {
    const formattedValue = formatter(value, {
      format: dateFormat,
      mapOptions
    });
    // If formatter returns a valid value, use it; otherwise fall back to original
    if (formattedValue) {
      return formattedValue;
    }
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(',');
  }

  // Return the original value as string
  return value;
};

/**
 * Get timesheet fields formatted for export with completed count
 * @param {Object} params - Parameters object
 * @param {Object} params.row - The row object containing mappedTimesheetFieldsValues
 * @param {Array} params.timesheetFields - Array of timesheet field definitions
 * @param {Function} params.t - Translation function
 * @param {string} params.dateFormat - Date format string
 * @returns {Object} - Object with sequencesResultsCompletedFieldsStatus and formatted field values
 */
export const getTimesheetFieldsForExport = ({ row, timesheetFields, t, dateFormat }) => {
  if (!timesheetFields || timesheetFields.length === 0) {
    return {};
  }

  const timesheetFieldsData = row?.mappedTimesheetFieldsValues || {};

  // Use the shared utility function for counting completed fields
  const completedFields = getTimesheetFieldsCompletedCount(row);

  const formattedTimesheetFields = timesheetFields.reduce((output, field) => {
    const value = timesheetFieldsData[field.id];
    const fieldName = field.isDeleted ? `${field.name} (${t('deleted')})` : field.name;
    output[fieldName] = formatTimesheetFieldValueForExport(field, value, { t, dateFormat });
    return output;
  }, {});

  return {
    sequencesResultsCompletedFieldsStatus: completedFields || '',
    ...formattedTimesheetFields
  };
};
